/**
 * API Client with JWT Authentication and Auto-Refresh
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_URL, ENDPOINTS, Promo, PromoValidation } from './constants';
import { getFingerprint } from './fingerprint';
import { logger } from './logger';

// Create axios instance
export const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
let accessToken: string | null = null;
let refreshToken: string | null = null;
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: Error | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

export function setTokens(access: string, refresh: string) {
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem('accessToken', access);
  localStorage.setItem('refreshToken', refresh);
}

export function getAccessToken(): string | null {
  if (!accessToken) {
    accessToken = localStorage.getItem('accessToken');
  }
  return accessToken;
}

export function getRefreshToken(): string | null {
  if (!refreshToken) {
    refreshToken = localStorage.getItem('refreshToken');
  }
  return refreshToken;
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

// Request interceptor: attach JWT and device fingerprint
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Attach device fingerprint for security
    const fingerprint = await getFingerprint();
    config.headers['X-Device-Fingerprint'] = fingerprint;

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 and auto-refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for the refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refresh = getRefreshToken();
      if (!refresh) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_URL}${ENDPOINTS.AUTH.REFRESH}`, {
          refreshToken: refresh,
        });

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;
        setTokens(newAccessToken, newRefreshToken);

        processQueue(null);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error);
        isRefreshing = false;
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Auth API
 */
export const authApi = {
  /**
   * Send OTP to phone number
   */
  sendOTP: async (phone: string) => {
    const response = await api.post(ENDPOINTS.AUTH.SEND_OTP, { phone, role: 'buyer' });
    return response.data;
  },

  /**
   * Verify OTP
   */
  verifyOTP: async (phone: string, otp: string) => {
    const response = await api.post(ENDPOINTS.AUTH.VERIFY_OTP, { phone, otp });
    
    // Backend returns: { success: true, data: { access_token, user } }
    const { access_token, user } = response.data.data || response.data;
    
    // Use access token for both (backend doesn't provide refresh token yet)
    setTokens(access_token, access_token);
    return { user, accessToken: access_token, refreshToken: access_token };
  },

  /**
   * Logout
   */
  logout: async () => {
    try {
      await api.post(ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      logger.error('Logout failed', error as Error);
    } finally {
      clearTokens();
    }
  },
};

/**
 * Zones API
 */
export const zonesApi = {
  /**
   * Get all zones
   */
  getZones: async () => {
    const response = await api.get(ENDPOINTS.ZONES.LIST);
    return response.data.zones;
  },

  /**
   * Get zone details
   */
  getZone: async (zoneId: string) => {
    const endpoint = ENDPOINTS.ZONES.DETAILS.replace(':id', zoneId);
    const response = await api.get(endpoint);
    return response.data.zone;
  },
};

/**
 * Vendors API
 */
export const vendorsApi = {
  /**
   * Search vendors
   */
  search: async (params: {
    zone?: string;
    q?: string;
    category?: string;
    open_now?: boolean;
    sort?: 'rating' | 'distance' | 'delivery_time';
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get(ENDPOINTS.VENDORS.SEARCH, { params });
    return response.data.vendors || [];
  },

  /**
   * Get vendor details
   */
  getVendor: async (vendorId: string) => {
    const endpoint = ENDPOINTS.VENDORS.DETAILS.replace(':id', vendorId);
    const response = await api.get(endpoint);
    return response.data.vendor;
  },

  /**
   * Get vendor menu
   */
  getMenu: async (vendorId: string) => {
    const endpoint = ENDPOINTS.VENDORS.MENU.replace(':id', vendorId);
    const response = await api.get(endpoint);
    return response.data.menu;
  },
};

/**
 * Orders API
 */
export const ordersApi = {
  /**
   * Get buyer orders
   */
  getOrders: async (params?: { status?: string; limit?: number; offset?: number }) => {
    const response = await api.get(ENDPOINTS.ORDERS.LIST, { params });
    // API returns { orders: [...], pagination: {...} }
    return response.data.orders || response.data;
  },

  /**
   * Create order (Sprint 4C format)
   */
  createOrder: async (orderData: {
    vendor_id: string;
    address: {
      line1: string;
      barangay: string;
      city: string;
      lat: number;
      lng: number;
    };
    cart: {
      items: Array<{ product_id: string; qty: number; options?: Array<{ name: string; value: string }> }>;
    };
    payment_method: string;
    promo_code?: string;
  }) => {
    const response = await api.post(ENDPOINTS.ORDERS.CREATE, orderData, {
      headers: {
        'Idempotency-Key': `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      },
    });
    return response.data.order;
  },

  /**
   * Get order details
   */
  getOrder: async (orderId: string) => {
    const endpoint = ENDPOINTS.ORDERS.DETAILS.replace(':id', orderId);
    const response = await api.get(endpoint);
    return response.data.order;
  },

  /**
   * Cancel order
   */
  cancelOrder: async (orderId: string, reason: string) => {
    const endpoint = ENDPOINTS.ORDERS.CANCEL.replace(':id', orderId);
    const response = await api.post(endpoint, { reason });
    return response.data.order;
  },
};

/**
 * Promos API
 */
export const promosApi = {
  /**
   * Get available promos
   */
  getPromos: async () => {
    const response = await api.get(ENDPOINTS.PROMOS.LIST);
    return response.data.promos as Promo[];
  },

  /**
   * Apply promo code (validate and calculate discount)
   */
  applyPromo: async (promoCode: string, orderData: {
    vendor_id: string;
    order_total: number;
    payment_method: string;
  }): Promise<PromoValidation> => {
    try {
      const response = await api.post(ENDPOINTS.PROMOS.APPLY, {
        code: promoCode,
        order_total: orderData.order_total,
        payment_method: orderData.payment_method,
      });
      return {
        valid: response.data.valid,
        discount: response.data.discount,
        message: response.data.promo?.description,
      };
    } catch (error: any) {
      // Handle specific error responses from backend
      const errorData = error.response?.data;
      if (errorData?.reason || errorData?.error) {
        return {
          valid: false,
          discount: 0,
          message: errorData.message || 'Promo code invalid',
          reason: errorData.reason || errorData.error,
          audit_id: errorData.audit_id,
        };
      }
      throw error;
    }
  },

  /**
   * Validate promo code (check if applicable)
   */
  validatePromo: async (promoCode: string, orderData?: {
    order_total?: number;
  }): Promise<PromoValidation> => {
    try {
      const response = await api.post(ENDPOINTS.PROMOS.VALIDATE, {
        code: promoCode,
        order_total: orderData?.order_total,
      });
      return {
        valid: response.data.valid,
        discount: 0,
        message: response.data.promo?.description,
      };
    } catch (error: any) {
      const errorData = error.response?.data;
      if (errorData?.reason) {
        return {
          valid: false,
          discount: 0,
          message: errorData.message || 'Promo code invalid',
          reason: errorData.reason,
          audit_id: errorData.audit_id,
        };
      }
      throw error;
    }
  },
};

/**
 * Chat API (Sprint 4D)
 */
export async function getChatThreads() {
  const response = await api.get('/api/chat/threads');
  return response.data;
}

export async function createChatThread(orderId: string) {
  const response = await api.post('/api/chat/threads', { order_id: orderId });
  return response.data;
}

export async function getChatMessages(threadId: string, cursor?: string, limit = 50) {
  const response = await api.get(`/api/chat/messages/${threadId}`, {
    params: { cursor, limit },
  });
  return response.data;
}

export async function sendChatMessage(threadId: string, message: string) {
  const response = await api.post(`/api/chat/messages/${threadId}`, {
    message,
    attachments: [],
  });
  return response.data;
}

export async function sendTypingIndicator(threadId: string, isTyping: boolean) {
  const response = await api.post(`/api/chat/typing/${threadId}`, {
    is_typing: isTyping,
  });
  return response.data;
}

export async function markMessagesAsRead(threadId: string, messageId: number) {
  const response = await api.post(`/api/chat/read/${threadId}`, {
    message_id: messageId,
  });
  return response.data;
}

/**
 * Payment API (Sprint 4D)
 */
export async function createPaymentIntent(orderId: string, paymentMethod: 'gcash' | 'paymaya' | 'cod') {
  const response = await api.post('/api/payments/intent', {
    order_id: orderId,
    payment_method: paymentMethod,
  });
  return response.data;
}

export async function verifyPayment(intentId: string, externalTransactionId?: string) {
  const response = await api.post('/api/payments/verify', {
    intent_id: intentId,
    external_transaction_id: externalTransactionId,
  });
  return response.data;
}

export async function getPaymentIntent(intentId: string) {
  const response = await api.get(`/api/payments/intent/${intentId}`);
  return response.data;
}

/**
 * GPS Tracking API (Sprint 4E)
 */
export async function getOrderGPS(orderId: string) {
  const response = await api.get(`/api/orders/${orderId}/gps`);
  return response.data;
}

export async function getOrderGPSHistory(orderId: string, minutes = 30, limit = 100) {
  const response = await api.get(`/api/orders/${orderId}/gps/history`, {
    params: { minutes, limit },
  });
  return response.data;
}

export async function getOrderRoute(orderId: string) {
  const response = await api.get(`/api/orders/${orderId}/route`);
  return response.data;
}

// Legacy chatApi for backward compatibility
export const chatApi = {
  createOrGetThread: createChatThread,
  getMessages: getChatMessages,
  sendMessage: sendChatMessage,
  sendTyping: sendTypingIndicator,
};

/**
 * Ratings API (Sprint 4I)
 */
export const ratingsApi = {
  /**
   * Create or update rating
   */
  createRating: async (data: {
    order_id: string;
    target_type: 'vendor' | 'rider';
    target_id: string;
    rating_value: number;
    comment?: string;
  }) => {
    const response = await api.post(ENDPOINTS.RATINGS.CREATE, data);
    return response.data.data;
  },
  
  /**
   * Get ratings for an order
   */
  getOrderRatings: async (orderId: string) => {
    const endpoint = ENDPOINTS.RATINGS.GET_ORDER.replace(':orderId', orderId);
    const response = await api.get(endpoint);
    return response.data.data;
  },
  
  /**
   * Get aggregated rating for vendor/rider
   */
  getAggregated: async (type: 'vendor' | 'rider', id: string) => {
    const endpoint = ENDPOINTS.RATINGS.GET_AGGREGATED
      .replace(':type', type)
      .replace(':id', id);
    const response = await api.get(endpoint);
    return response.data.data;
  },
};

/**
 * Media API (Sprint 4I)
 */
export const mediaApi = {
  /**
   * Upload review photo
   */
  upload: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(ENDPOINTS.MEDIA.UPLOAD, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000, // 30s for uploads
    });
    
    return response.data.data;
  },
  
  /**
   * Delete media
   */
  delete: async (mediaId: string) => {
    const endpoint = ENDPOINTS.MEDIA.DELETE.replace(':id', mediaId);
    const response = await api.delete(endpoint);
    return response.data.data;
  },
  
  /**
   * Get media details
   */
  get: async (mediaId: string) => {
    const endpoint = ENDPOINTS.MEDIA.GET.replace(':id', mediaId);
    const response = await api.get(endpoint);
    return response.data.data;
  },
};

/**
 * Search API (Sprint 4J)
 */
export const searchApi = {
  /**
   * Search vendors
   */
  vendors: async (params: {
    q?: string;
    category?: string;
    zone_id?: string;
    sort_by?: string;
    page?: number;
    limit?: number;
    buyer_lat?: number;
    buyer_lng?: number;
  }) => {
    const response = await api.get('/api/search/vendors', { params });
    return response.data.data;
  },

  /**
   * Search products
   */
  products: async (params: {
    q?: string;
    category?: string;
    vendor_id?: string;
    zone_id?: string;
    sort_by?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get('/api/search/products', { params });
    return response.data.data;
  },

  /**
   * Get categories
   */
  categories: async (zone_id?: string) => {
    const response = await api.get('/api/search/categories', { params: { zone_id } });
    return response.data.data;
  },

  /**
   * Get autocomplete suggestions
   */
  autocomplete: async (q: string, type?: string) => {
    const response = await api.get('/api/search/autocomplete', { params: { q, type } });
    return response.data.data;
  },
};

/**
 * Cart API (Sprint 4J)
 */
export const cartApi = {
  /**
   * Add item to cart
   */
  addItem: async (product_id: string, quantity: number) => {
    const response = await api.post('/api/cart/items', { product_id, quantity });
    return response.data.data;
  },

  /**
   * Update cart item quantity
   */
  updateItem: async (cart_item_id: string, quantity: number) => {
    const response = await api.put(`/api/cart/items/${cart_item_id}`, { quantity });
    return response.data.data;
  },

  /**
   * Remove item from cart
   */
  removeItem: async (cart_item_id: string) => {
    const response = await api.delete(`/api/cart/items/${cart_item_id}`);
    return response.data.data;
  },

  /**
   * Clear cart
   */
  clear: async () => {
    const response = await api.delete('/api/cart');
    return response.data.data;
  },

  /**
   * Get cart summary
   */
  get: async (buyer_lat?: number, buyer_lng?: number) => {
    const response = await api.get('/api/cart', { params: { buyer_lat, buyer_lng } });
    return response.data.data;
  },

  /**
   * Validate cart before checkout
   */
  validate: async () => {
    const response = await api.post('/api/cart/validate');
    return response.data.data;
  },
};

export default api;

